import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Feedback for the newsletter double opt-in confirmation link.
 *
 * The confirmation email points at the API (`GET /api/newsletter/confirm`),
 * which does the work and then redirects here with `?newsletter=confirmed`
 * (or `expired` / `invalid`). Without this the subscriber landed on the
 * homepage with a query param and no indication that anything had happened —
 * they'd confirmed successfully but had no way to know it.
 *
 * `expired` also covers a token that was already used, since the API clears the
 * token on confirm; "subscribe again" is the right advice either way (the
 * endpoint is idempotent for an already-confirmed address).
 *
 * Styling is inline and self-contained: the site's CSS is a vendored Webflow
 * export, so it takes the brand palette from the :root custom properties
 * rather than adding rules to that file.
 */

const VALID_STATUSES = ["confirmed", "expired", "invalid"];

const NewsletterConfirmation = () => {
  const { t } = useTranslation();
  const bannerRef = useRef(null);

  // Read on first render rather than in an effect — the param is already in the
  // URL at mount, so there's no reason to render once without it.
  const [status, setStatus] = useState(() => {
    const value = new URLSearchParams(window.location.search).get("newsletter");
    return VALID_STATUSES.includes(value) ? value : null;
  });

  // The site's navbar is `position: fixed` with `z-index: 2000`, so anything
  // pinned near the top is painted over by it. Rather than out-bid it — which
  // would drop this card on top of the logo — sit just underneath it. The
  // height is measured because it differs between mobile and desktop; the
  // inline `top` below is the fallback if the navbar isn't on the page.
  useEffect(() => {
    if (!status) return;
    const frame = requestAnimationFrame(() => {
      const nav = document.querySelector(".navbar");
      const height = nav ? nav.getBoundingClientRect().height : 0;
      if (height > 0 && bannerRef.current) {
        bannerRef.current.style.top = `${height + 12}px`;
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [status]);

  useEffect(() => {
    if (!status) return;

    // Drop the param so a refresh — or a URL pasted to someone else — doesn't
    // replay the message. Other params (e.g. ?lang=) are preserved; the i18n
    // detector has already read them by this point.
    const params = new URLSearchParams(window.location.search);
    if (!params.has("newsletter")) return;
    params.delete("newsletter");
    const query = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (query ? `?${query}` : "") + window.location.hash,
    );
    // Runs once on mount: strips the param from the address bar. Dismissing the
    // message later must not re-run it, so `status` is deliberately not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!status) return null;

  const isConfirmed = status === "confirmed";

  return (
    <div
      ref={bannerRef}
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        top: "1.25rem",
        left: "1rem",
        right: "1rem",
        zIndex: 999,
        maxWidth: "34rem",
        margin: "0 auto",
        display: "flex",
        alignItems: "flex-start",
        gap: "0.875rem",
        padding: "1.25rem 1.375rem",
        backgroundColor: "var(--white)",
        border: "1px solid var(--light-grey)",
        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
      }}
    >
      <span aria-hidden="true" style={{ flexShrink: 0, lineHeight: 0, marginTop: "0.125rem" }}>
        {isConfirmed ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <path d="M7.5 12.5l3 3 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 7.5v5" strokeLinecap="round" />
            <circle cx="12" cy="16.25" r="0.85" fill="currentColor" stroke="none" />
          </svg>
        )}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, marginBottom: "0.25rem", color: "var(--black)" }}>
          {t(`footer.newsletter.confirm.${status}.title`)}
        </div>
        <div style={{ fontSize: "0.9rem", lineHeight: 1.5, color: "var(--grey)" }}>
          {t(`footer.newsletter.confirm.${status}.body`)}
        </div>
      </div>

      <button
        type="button"
        onClick={() => setStatus(null)}
        aria-label={t("footer.newsletter.confirm.close")}
        style={{
          flexShrink: 0,
          background: "none",
          border: "none",
          padding: "0.25rem",
          cursor: "pointer",
          lineHeight: 0,
          color: "var(--grey)",
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
};

export default NewsletterConfirmation;
